import { Viewer } from "@nova/schemas"
import { Builder } from "."
import { SocketSlice } from "./types"
import { selectActivatedFire } from "features/map/control/selectors"
import { mapStateReducerKeys, RootState } from "integrations/redux/rootReducer"
import {
    removeViewer,
    subscribeToMap,
    unsubscribeToMap,
    addViewer,
    receiveState,
    publishState,
    copyState,
    setWatching,
    pubsubTriggerNotification,
    selectTriggerNotification,
    selectWatching,
    addSubscriber,
    removeSubscriber,
    setUpdatedViewers,
} from "../slice"
import { AnyAction, PartialViewState } from "@nova/constants"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { selectFilteredTagIds } from "features/data-management/workspace/WorkspaceManagementSlice"
import { loadProject } from "features/map/control/thunks"
import { selectUserID } from "user/profile/selectors"
import { get } from "nodash"
import { getViewState } from "features/map/GlobalViewState"
import { createAction } from "@reduxjs/toolkit"
import { invalidatePendingLiveStreams } from "plugins/Video/LiveVideo/usePendingLiveStreams"

let cachedState: { reduxState: RootState; viewState: PartialViewState } | undefined
let watching: Viewer | undefined = undefined

export const clearPubsubSocketsMapState = createAction("clearPubsubSocketsState")

export default {
    handlers: (builder: Builder) =>
        builder
            .addMatcher([loadProject.fulfilled], (store, action, socket) => {
                const fireId = get(action, "meta.arg.projectId") as string
                const orgId = get(action, "meta.arg.orgId") as string
                const tree = selectFilteredTagIds()(store.getState())
                const userId = selectUserID(store.getState())
                if (!orgId || !fireId) {
                    console.error(
                        "Missing important properties after loading to project",
                        orgId,
                        fireId
                    )
                    return
                }

                const ids: string[] = [orgId, ...tree, fireId]
                socket.emit("enterMap", ids)
                const existingViewers = get(
                    selectActivatedFire(store.getState()),
                    "viewers",
                    []
                ) as Viewer[]
                existingViewers.forEach((viewer) => {
                    if (viewer.userId !== userId) store.dispatch(addViewer(viewer))
                })
            })
            .addCase(publishState, (store, _, socket) => {
                const state = store.getState()
                const newState = mapStateReducerKeys.reduce((acc, cur) => {
                    const key = cur as keyof typeof state
                    acc[key] = state[key]
                    return acc
                }, {} as AnyAction)
                socket.emit("publishState", { reduxState: newState, viewState: getViewState() })
            })
            .addCase(subscribeToMap, (_, action, socket) => {
                socket.emit("subscribeToUser", action.payload.id)
                watching = action.payload
            })
            .addCase(copyState, (_, action, socket) => {
                socket.emit("unsubscribeToUser", action.payload, "state")
                watching = undefined
                cachedState = undefined
            })
            .addCase(unsubscribeToMap, (store, action, socket) => {
                socket.emit("unsubscribeToUser", action.payload, "state")
                if (cachedState) store.dispatch(receiveState(cachedState as AnyAction))
                store.dispatch(pubsubTriggerNotification({}))
                watching = undefined
                cachedState = undefined
            })
            .addCase(clearPubsubSocketsMapState, (store, _, socket) => {
                const watching = selectWatching()(store.getState())
                if (watching) store.dispatch(unsubscribeToMap())
                socket.emit("leaveMap")
            }),
    events: {
        pendingLiveStreamsChanged: () => () => {
            void invalidatePendingLiveStreams()
        },
        state: (store) => async (_: string, data: Partial<RootState>) => {
            const state = store.getState()
            if (!cachedState)
                cachedState = {
                    reduxState: JSON.parse(
                        JSON.stringify(
                            mapStateReducerKeys.reduce((acc, cur) => {
                                const key = cur as keyof typeof state
                                acc[key] = state[key]
                                return acc
                            }, {} as AnyAction)
                        )
                    ),
                    viewState: getViewState(),
                }

            store.dispatch(receiveState(data as AnyAction))
            const showNotification = selectTriggerNotification()(state)
            if (!showNotification) {
                store.dispatch(pubsubTriggerNotification({ viewer: watching }))
            }
        },
        unsubscribe: (store) => () => {
            if (cachedState) store.dispatch(receiveState(cachedState as AnyAction))
            store.dispatch(setWatching(undefined))
            cachedState = undefined
            watching = undefined
        },
        newViewer: (store, socket) => (user: Viewer) => {
            // Make sure the new viewer isn't this client itself.
            if (user.id !== socket.id) store.dispatch(addViewer(user))
        },
        removeViewer: (store, socket) => (userId: string) => {
            // Make sure the removed viewer isn't this client itself.
            if (userId !== socket.id) store.dispatch(removeViewer(userId))
        },
        updatedViewers: (store, socket) => (viewers: Viewer[]) => {
            store.dispatch(setUpdatedViewers(viewers.filter((e) => e.id !== socket.id)))
        },
        newSubscriber: (store, socket) => (user: Viewer) => {
            if (user.id !== socket.id) store.dispatch(addSubscriber(user))
        },
        removeSubscriber: (store, socket) => (userId: string) => {
            if (userId !== socket.id) store.dispatch(removeSubscriber(userId))
        },
        failure: (store) => (user: string) => {
            const state = store.getState()
            if (selectWatching()(state)?.id === user) {
                store.dispatch(unsubscribeToMap())
                store.dispatch(
                    pubsubTriggerNotification({
                        customContext: {
                            message: `Failed to follow the person due to inactivity`,
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        },
                    })
                )
            }
        },
    },
} as SocketSlice
